// DNC Lists — list + CSV import (one number per line / first column).
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Upload, Loader2, Ban, ListOrdered, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function DNCListsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();
  const { user } = useAuth();
  const canManageNumbers = user?.role === "super_admin" || user?.role === "super_super_admin";

  // ---- individual-number manager ----
  const [numbersList, setNumbersList] = useState(null); // the DNC list being viewed
  const [entries, setEntries] = useState([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesSearch, setEntriesSearch] = useState("");
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [addingNumber, setAddingNumber] = useState(false);
  const LIMIT = 50;

  // ---- number lookup / unblock ----
  const [lookupPhone, setLookupPhone] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [unblocking, setUnblocking] = useState(false);

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/resources/dnc_lists"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() { setForm({ name: "", description: "", is_active: true, region: "IN", auto_sync_days: 0 }); setEditing("new"); }
  function startEdit(r) { setForm({ name: r.name, description: r.description||"", is_active: !!r.is_active, region: r.data?.region||"IN", auto_sync_days: r.data?.auto_sync_days||0 }); setEditing(r); }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, is_active: !!form.is_active, data: { region: form.region, auto_sync_days: Number(form.auto_sync_days)||0 } };
      if (editing === "new") await api.post("/admin/resources/dnc_lists", payload);
      else await api.patch(`/admin/resources/dnc_lists/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}" and ALL its numbers?`)) return;
    try { await api.delete(`/admin/resources/dnc_lists/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  async function importCSV(id, file) {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/dnc-lists/${id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${r.data.inserted} numbers (${r.data.duplicates_skipped} dups). Total: ${r.data.total}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function loadEntries(list, page = 1, search = "") {
    if (!list) return;
    setEntriesLoading(true);
    try {
      const r = await api.get(`/admin/dnc-lists/${list.id}/entries`, { params: { page, limit: LIMIT, search } });
      setEntries(r.data.items || []);
      setEntriesTotal(r.data.total || 0);
      setEntriesPage(page);
    } catch (e) { toast.error(formatErr(e)); } finally { setEntriesLoading(false); }
  }
  function openNumbers(list) {
    setNumbersList(list); setEntriesSearch(""); setNewNumber("");
    loadEntries(list, 1, "");
  }
  async function addNumber() {
    const n = (newNumber || "").trim();
    if (!n) { toast.error("Enter a number"); return; }
    setAddingNumber(true);
    try {
      await api.post(`/admin/dnc-lists/${numbersList.id}/entries`, { phone: n });
      toast.success("Number added"); setNewNumber("");
      loadEntries(numbersList, 1, entriesSearch); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setAddingNumber(false); }
  }
  async function deleteNumber(entry) {
    if (!confirm(`Remove ${entry.phone} from "${numbersList.name}"? It will be dialable again.`)) return;
    try {
      const r = await api.delete(`/admin/dnc-lists/${numbersList.id}/entries/${entry.id}`);
      toast.success(`Removed. ${r.data.leads_reactivated || 0} lead(s) reactivated.`);
      loadEntries(numbersList, entriesPage, entriesSearch); load();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function runLookup() {
    const p = (lookupPhone || "").trim();
    if (!p) { toast.error("Enter a number to check"); return; }
    setLookupLoading(true); setLookupResult(null);
    try {
      const r = await api.get("/admin/dnc/lookup", { params: { phone: p } });
      setLookupResult(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLookupLoading(false); }
  }
  async function unblockNumber() {
    const p = (lookupPhone || "").trim();
    if (!p) return;
    if (!confirm(`Unblock ${p}? This removes it from ALL DNC lists and resets any lead stuck on 'dnc' so it dials again.`)) return;
    setUnblocking(true);
    try {
      const r = await api.post("/admin/dnc/unblock", { phone: p });
      toast.success(`Unblocked: removed ${r.data.entries_removed} DNC entr(ies), reactivated ${r.data.leads_reactivated} lead(s).`);
      runLookup(); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setUnblocking(false); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="dnc-title">Do-Not-Call Lists</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload phone numbers to block. Campaigns honor referenced lists automatically.</p>
        </div>
        {canWrite && <Button data-testid="add-dnc-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New DNC list</Button>}
      </div>

      {canManageNumbers && (
        <Card className="p-4 mb-5 border-primary/30" data-testid="dnc-lookup-card">
          <div className="flex items-center gap-2 mb-1"><Search className="w-4 h-4 text-primary"/><h2 className="font-semibold text-sm">Why is a number blocked? — Lookup & Unblock</h2></div>
          <p className="text-xs text-muted-foreground mb-3">A number can be blocked by a DNC list OR by a lead marked "dnc" (e.g. an agent dispositioned it as DNC). Check both here and unblock in one click.</p>
          <div className="flex items-center gap-2">
            <Input data-testid="dnc-lookup-input" className="h-9 max-w-xs" placeholder="Enter phone number…" value={lookupPhone}
                   onChange={(e)=>setLookupPhone(e.target.value)}
                   onKeyDown={(e)=>{ if (e.key==="Enter") runLookup(); }} />
            <Button data-testid="dnc-lookup-btn" variant="outline" className="h-9" onClick={runLookup} disabled={lookupLoading}>{lookupLoading?<Loader2 className="w-4 h-4 animate-spin"/>:"Check"}</Button>
            <Button data-testid="dnc-unblock-btn" className="h-9 bg-destructive hover:bg-destructive/90 text-white gap-1.5" onClick={unblockNumber} disabled={unblocking || !lookupResult}>{unblocking?<Loader2 className="w-4 h-4 animate-spin"/>:<Ban className="w-4 h-4"/>} Unblock everywhere</Button>
          </div>

          {lookupResult && (
            <div className="mt-3 text-sm space-y-2" data-testid="dnc-lookup-result">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Status:</span>
                {lookupResult.is_dnc || lookupResult.leads?.some(l=>l.status==="dnc")
                  ? <Badge variant="destructive" data-testid="dnc-lookup-blocked">BLOCKED</Badge>
                  : <Badge variant="outline" className="text-green-600 border-green-600" data-testid="dnc-lookup-clear">Not blocked — dialable</Badge>}
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">In DNC lists ({lookupResult.dnc_entries?.length || 0})</div>
                {lookupResult.dnc_entries?.length ? (
                  <ul className="text-xs space-y-0.5">{lookupResult.dnc_entries.map((e)=>(<li key={e.id}>• <span className="font-mono">{e.phone}</span> in <span className="font-medium">{e.list_name}</span></li>))}</ul>
                ) : <div className="text-xs text-muted-foreground">Not in any DNC list.</div>}
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Lead rows ({lookupResult.leads?.length || 0})</div>
                {lookupResult.leads?.length ? (
                  <ul className="text-xs space-y-0.5">{lookupResult.leads.map((l)=>(<li key={l.id}>• <span className="font-mono">{l.phone}</span> in <span className="font-medium">{l.list_name}</span> — status <Badge variant={l.status==="dnc"?"destructive":"outline"} className="text-[10px]">{l.status}</Badge>{l.status==="dnc" && l.last_disposition_name ? <span className="text-muted-foreground"> (disposition: {l.last_disposition_name})</span> : null}</li>))}</ul>
                ) : <div className="text-xs text-muted-foreground">No lead rows with this number.</div>}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Region</th><th className="px-4 py-2">Numbers</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-64">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-dnc-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Ban className="w-4 h-4 text-destructive"/><span className="font-medium">{r.name}</span></div>{r.description && <div className="text-xs text-muted-foreground ml-6">{r.description}</div>}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{r.data?.region||"—"}</Badge></td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{r.data?.size ?? 0}</Badge></td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">Active</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-1">
                    <label className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border text-sm hover:bg-muted cursor-pointer" data-testid={`import-dnc-${r.id}`}>
                      <Upload className="w-3.5 h-3.5"/> Import
                      <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={(e)=>{const f=e.target.files?.[0]; if (f) importCSV(r.id, f); e.target.value="";}} />
                    </label>
                    {canWrite && <Button data-testid={`edit-dnc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canManageNumbers && <Button data-testid={`numbers-dnc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" title="View / edit numbers" onClick={()=>openNumbers(r)}><ListOrdered className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`del-dnc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-muted-foreground">No DNC lists yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing==="new"?"New DNC list":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>After creating, click "Import" to upload numbers.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input data-testid="dnc-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Description</Label><Input data-testid="dnc-desc" value={form.description||""} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Region</Label>
                <Select value={form.region} onValueChange={(v)=>setForm({...form, region:v})}>
                  <SelectTrigger data-testid="dnc-region" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>{["IN","US","UK","AE","SG","AU","ALL"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Auto-sync days (0=manual)</Label><Input data-testid="dnc-sync" type="number" value={form.auto_sync_days||0} onChange={(e)=>setForm({...form, auto_sync_days:Number(e.target.value)})} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="dnc-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="dnc-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual-number manager (super admins) */}
      <Dialog open={!!numbersList} onOpenChange={(v)=>!v&&setNumbersList(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="w-4 h-4 text-destructive"/> Numbers in {numbersList?.name}</DialogTitle>
            <DialogDescription>View, search, add or remove blocked numbers. Removing a number lets it be dialed again.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input data-testid="dnc-entry-search" className="pl-8 h-9" placeholder="Search number…" value={entriesSearch}
                     onChange={(e)=>setEntriesSearch(e.target.value)}
                     onKeyDown={(e)=>{ if (e.key==="Enter") loadEntries(numbersList,1,entriesSearch); }} />
            </div>
            <Button data-testid="dnc-entry-search-btn" variant="outline" className="h-9" onClick={()=>loadEntries(numbersList,1,entriesSearch)}>Search</Button>
          </div>

          <div className="flex items-center gap-2">
            <Input data-testid="dnc-entry-new" className="h-9" placeholder="Add a number to block…" value={newNumber}
                   onChange={(e)=>setNewNumber(e.target.value)}
                   onKeyDown={(e)=>{ if (e.key==="Enter") addNumber(); }} />
            <Button data-testid="dnc-entry-add-btn" className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" onClick={addNumber} disabled={addingNumber}>
              {addingNumber ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>} Add
            </Button>
          </div>

          <Card className="overflow-hidden max-h-[46vh] overflow-y-auto">
            {entriesLoading ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div> : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground sticky top-0"><tr className="text-left">
                  <th className="px-4 py-2">Phone</th><th className="px-4 py-2">Added</th><th className="px-4 py-2 w-20 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {entries.map((en)=>(
                    <tr key={en.id} className="border-t border-border hover:bg-muted/30" data-testid={`dnc-entry-${en.id}`}>
                      <td className="px-4 py-2 font-mono">{en.phone}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{en.added_at ? new Date(en.added_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                      <td className="px-4 py-2 text-right"><Button data-testid={`dnc-entry-del-${en.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>deleteNumber(en)}><Trash2 className="w-4 h-4"/></Button></td>
                    </tr>
                  ))}
                  {entries.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-sm text-muted-foreground">No numbers found.</td></tr>}
                </tbody>
              </table>
            )}
          </Card>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="dnc-entry-total">{entriesTotal} number(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={entriesPage<=1} onClick={()=>loadEntries(numbersList, entriesPage-1, entriesSearch)}>Prev</Button>
              <span>Page {entriesPage} / {Math.max(1, Math.ceil(entriesTotal/LIMIT))}</span>
              <Button variant="outline" size="sm" disabled={entriesPage>=Math.ceil(entriesTotal/LIMIT)} onClick={()=>loadEntries(numbersList, entriesPage+1, entriesSearch)}>Next</Button>
            </div>
          </div>

          <DialogFooter><Button variant="outline" onClick={()=>setNumbersList(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
